import React, { useCallback, useRef, useState } from "react";
import { Autocomplete, Popper, TextField } from "@mui/material";
import debounce from "lodash.debounce";

const DebouncedAutocomplete = ({
  label = "Pesquisar",
  fetchOptions,
  onSelect,
  getOptionLabel,
  getOptionValue,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const isSelecting = useRef(false); // Track whether an option is being selected

  // Debounced function to fetch options
  const debouncedFetchOptions = useCallback(
    debounce(async (query) => {
      if (!isSelecting.current) {
        setLoading(true);
        const results = await fetchOptions(query);
        setOptions(results);
        setLoading(false);
      }
    }, 500),
    [fetchOptions]
  );

  // Handle input value changes
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    if (!isSelecting.current && newInputValue.trim()) {
      debouncedFetchOptions(newInputValue);
    } else if (!newInputValue.trim()) {
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      loading={loading}
      value={null} // Controlled value for selected option
      onChange={(event, selectedOption) => {
        if (selectedOption) {
          isSelecting.current = true; // Disable fetching temporarily
          onSelect(getOptionValue(selectedOption)); // Notify parent about selection
          setInputValue(getOptionLabel(selectedOption)); // Update input value with label
          setTimeout(() => (isSelecting.current = false), 300); // Re-enable fetching after a delay
        }
      }}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel} // Pass a function to get the label
      noOptionsText={"Nada encontrado"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <span>Loading...</span> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={(props) => (
        <Popper {...props} style={{ zIndex: 9999 }} />
      )}
    />
  );
};

export default DebouncedAutocomplete;
